import { Component, Input, Output, inject } from '@angular/core';
import { Subject } from 'rxjs';
import { htmlToMarkdown, MessageDialogComponent, Shell } from '@tytapp/common';

@Component({
    selector: 'tyt-markdown-field',
    template: `
        <div style="text-align: right; font-size: 10px; position: absolute; right: 3em; top: 1.5em; z-index: 100;">
            <a matTooltip="Learn how to use Markdown" href="javascript:;" (click)="markdownReference()">(Reference)</a>
        </div>
        <mat-form-field class="required" floatLabel="always">
            <mat-label>{{ label }}</mat-label>
            <textarea name="text" matInput
                    autosize
                    [ngModel]="value"
                    (ngModelChange)="valueChange.next($event)"
                    [placeholder]="placeholder"
                    [disabled]="disabled"
                    (keydown)="keydown.next($event)"
                    (paste)="handlePaste($event)"
            ></textarea>
        </mat-form-field>
    `,
    styles: [
        `
        :host {
            display: block;
            position: relative;
        }

        mat-form-field {
            width: 100%;
        }
        `
    ],
    standalone: false
})
export class MarkdownFieldComponent {
    private shell = inject(Shell);

    @Output() keydown = new Subject<KeyboardEvent>();
    @Input() label;
    @Input() placeholder = '';
    @Input() disabled = false;

    @Input() value: string;
    @Output() valueChange = new Subject<string>();

    handlePaste(event: ClipboardEvent) {
        // BUG-WORKAROUND
        // for https://github.com/crosstype/node-html-markdown/issues/36
        if (typeof process === 'undefined') {
            (window as any).process = { env: {} };
        }

        if (event.clipboardData.files.length > 0) {
            event.stopPropagation();
            event.preventDefault();
            alert(`You cannot paste files into an article block.`);
            return;
        }

        let html = event.clipboardData.getData('text/html');

        if (!html)
            return;

        event.stopPropagation();
        event.preventDefault();

        let markdown = htmlToMarkdown(html);
        let textarea = event.target as HTMLTextAreaElement;
        let selectionSpot = textarea.selectionStart;

        if (document.execCommand) {
            // inb4 this is removed, because this supports undo/redo
            document.execCommand?.("insertText", false, markdown);
        } else {
            // modern but no undo/redo
            textarea.setRangeText(markdown);
            textarea.setSelectionRange(selectionSpot + markdown.length, selectionSpot + markdown.length);
        }
    }

    markdownReference() {
        this.shell.showDialog(
            MessageDialogComponent,
            'Markdown Reference',
            `
            <p>Articles on TYT.com support rich text formatting via Markdown formatting tags.
                You can paste content from other applications and the editor will do its best
                to convert the formatting into Markdown, but it may not be perfect. Use the
                following guide to understand how to manually control the formatting of your
                article using Markdown.</p>

            <ul>
                <li>Inline styling: **Bold**, __Italic__, ++Underline++</li>
                <li>Links: [Link Text](https://example.com/)</li>
                <li>Heading 1: <code># Heading Text Here</code></li>
                <li>Heading 2: <code>## Heading Text Here</code></li>
                <li>Heading 3: <code>### Heading Text Here</code></li>
                <li>
                    Unordered lists:
                    <pre>
${''}* First item
${''}* Second item
${''}</pre>
                </li>
                <li>
                    Ordered lists:
                    <pre>
${''}1. First item
${''}2. Second item
${''}</pre>
                </li>
            </ul>
            `
        );
    }
}